/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import loadingConfig from "../config/loading.config.json";
import errorConfig from "../config/error.config.json";
import profileFormConfig from "../config/profileForm.config.json";
import formConfig from "../config/kycDocForm.config.json";
import config from "../config/featuresSection.config.json";
import notificationConfig from "../config/notifications.config.json";
import { StorageModule } from "@vlinder-web/storage-module-react";
import { toHeaderCase, toSentenceCase, toLowerCase } from "js-convert-case";

const lookup = require("country-code-lookup");

export interface IStaticPageContentMethods extends IPluginMethodMap {
  getStaticPageContentSync(args: any): any;
}

export class StaticPageContentService implements IAgentPlugin {
  readonly methods: IStaticPageContentMethods;

  constructor() {
    this.methods = {
      getStaticPageContentSync: this.getProps.bind(this),
    };
  }

  getProps(args: any) {
    let storageSrv = StorageModule?.getInstance();
    let _res = storageSrv?.get("myProfile", "session");
    return {
      loading: loadingConfig,
      error: errorConfig,
      profileForm: profileFormConfig,
      notifications: notificationConfig,
      myProfile: this.getMyprofile(_res),
    };
  }

  private getMyprofile(data: any) {
    if (data?.email)
      return {
        title: `${toHeaderCase(data?.firstName ?? "")} ${toHeaderCase(
          data?.lastName ?? ""
        )}`,
        subtitle: data?.vuid ? `Account ID: ${data?.vuid}` : data?.occupation,
        phoneNumber: data?.primaryContactNo,
        phoneVerified: data?.phoneVerified,
        kycSubmitted: data?.kycSubmitted,
        email: data?.email,
        heroImage: {
          image:
            data?.image ??
            `https://ui-avatars.com/api/?name=${data.firstName}+${data?.lastName}&background=0D8ABC&color=fff`,
        },
        listItems: this.getListItems(data),
        profileStatus: this.getStatus(data?.kyc),
        profileStatusDescription: this.getStatusDesc(data?.kyc),
        initialData: {
          firstName: data?.firstName,
          lastName: data?.lastName,
          image: data?.image,
          primaryContactNo: data?.primaryContactNo,
          occupation: data?.occupation,
          streetAddress: data?.address?.addressLine1,
          city: data?.address?.city,
          country: data?.address?.country,
        },
      };
  }

  private getListItems(res: any) {
    let _temp = [];
    _temp = config?.listItems?.map((item) => {
      if (item?.title === "Full Name") {
        return {
          ...item,
          subTitle: `${toHeaderCase(res?.firstName ?? "")} ${toHeaderCase(
            res?.lastName ?? ""
          )}`,
        };
      }
      if (item?.title === "Street Address") {
        return {
          ...item,
          subTitle: res?.address?.addressLine1
            ? `${res?.address?.addressLine1}`
            : "-",
        };
      }
      if (item?.title === "City") {
        return {
          ...item,
          subTitle: res?.address?.city ?? "-",
        };
      }
      if (item?.title === "Country") {
        return {
          ...item,
          subTitle: res?.address?.country
            ? lookup.byIso(`${res?.address?.country}`)?.country
            : "-",
          flag: res?.address?.country,
        };
      }
      if (item?.title === "Email") {
        return {
          ...item,
          subTitle: res?.email ?? "-",
          verified: res?.emailVerified,
        };
      }
      if (item?.title === "Telephone") {
        return {
          ...item,
          subTitle: res?.primaryContactNo ?? "-",
          verified: res?.phoneVerified,
        };
      }
      if (item?.title === "Occupation") {
        return {
          ...item,
          subTitle: res?.occupation ?? "-",
        };
      }
    });
    return _temp;
  }

  private getStatus(items: any) {
    let tempStatus = items && items[0];
    if (tempStatus?.status) {
      return toLowerCase(tempStatus?.status ?? "");
    } else return null;
  }

  private getStatusDesc(items: any) {
    let tempStatus = items && items[0];
    if (tempStatus?.comment) {
      return toSentenceCase(tempStatus?.comment ?? "");
    } else return null;
  }
}
